import React, { useState } from "react";
import axios from "axios";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Location from "../Location/Location";
import {
  FaLinkedin,
  FaInstagram,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaTwitter,
  FaYoutube,
  FaFacebookF,
} from "react-icons/fa";
import { FaPaperPlane, FaCheckCircle } from "react-icons/fa";
import { COMPANY_DATA } from "../../constant/CompanyData";
import { trackEvent } from "../../utils/analytics";
import ContactHelpOptions from "./ContactHelpOptions";

export function ContactSection() {
  const location = useLocation();
  const isContactRoute = location.pathname === "/contact";
  // One level below the section title (h1 standalone / h2 embedded) so the
  // "Request a consultation" heading stays sequential in both contexts.
  const SubHeadingTag = isContactRoute ? "h2" : "h3";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (!formData.name || !formData.email || !formData.message) {
      setErrorMessage("Please fill in all required fields.");
      setIsSubmitting(false);
      return;
    }

    const timestamp = new Date().toLocaleString();

    const googleSheetsUrl =
      "https://script.google.com/macros/s/AKfycbyZsPzIClTYsU7GWXDHbrbdobdpsNvJIVsqsaf4io12AOBivOTgSxNOwndzKdb_hTG9/exec";

    const formPayload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      timestamp: timestamp,
    };

    try {
      const response = await axios.post(googleSheetsUrl, null, {
        params: formPayload,
      });

      if (response.data !== "Success") {
        throw new Error("There was an error saving data to Google Sheets.");
      }

      // Step 2: Send email via EmailJS
      const emailTemplateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        reply_to: formData.email,
        timestamp: timestamp,
      };

      const emailResponse = await emailjs.send(
        "service_bdjqp8n",
        "template_i3ztwcb",
        emailTemplateParams,
        "8QM2nZ_tvq9ll0779"
      );

      if (emailResponse.status === 200) {
        trackEvent("generate_lead", { form_name: "contact_form" });
        setFormData({ name: "", email: "", phone: "", message: "" });
        setSuccessMessage(
          "Thank you! Your request has been received and our team will reach out soon."
        );

        setTimeout(() => {
          setSuccessMessage("");
        }, 5000);
      } else {
        setErrorMessage(
          "There was an error sending the email. Please try again."
        );
      }
    } catch (error) {
      setErrorMessage(
        "There was an error processing your request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isContactRoute && (
        <Helmet>
          <title>Contact Us | QuantaRock Systems</title>
          <meta
            name="description"
            content="Get in touch with QuantaRock Systems for colocation, dedicated servers, cloud hosting, and IT infrastructure solutions. Call, email, or send us a message."
          />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href="https://quantarock.in/contact" />
          <meta property="og:title" content="Contact Us | QuantaRock Systems" />
          <meta
            property="og:description"
            content="Get in touch with QuantaRock Systems for colocation, dedicated servers, cloud hosting, and IT infrastructure solutions."
          />
          <meta property="og:url" content="https://quantarock.in/contact" />
          <meta property="og:type" content="website" />
          <meta
            property="og:image"
            content="https://quantarock.in/og-image.jpg"
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content="Contact Us | QuantaRock Systems"
          />
          <meta
            name="twitter:description"
            content="Get in touch with QuantaRock Systems for colocation, dedicated servers, cloud hosting, and IT infrastructure solutions."
          />
        </Helmet>
      )}
      {isContactRoute && <ContactHelpOptions />}
      <section
        className="relative overflow-hidden"
        data-scroll-index="7"
      >
        <div
          className="animate-ken-burns absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${require(`../../assets/HeroSectionImages/colocation_services.webp`)})`,
          }}
        />
        <div className="absolute inset-0 bg-[rgba(var(--surface-dark-rgb),0.85)]" />
        <div className="relative py-20 md:py-24">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 lg:grid-cols-2">
            <div className="flex items-center justify-center px-6">
              <div className="contact-info max-w-xl text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--website-secondary-color)]">Let’s talk infrastructure</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">
                  We're Here to Help
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-slate-200">
                  Whether you're evaluating colocation, planning a migration,
                  or scoping a custom infrastructure project, our team is
                  ready to help. Share a few details and we'll get back to
                  you shortly.
                </p>
                <ul className="contact-info-list mt-8 space-y-5 text-slate-200">
                  <li className="flex items-center">
                    <div className="info-left mr-4 text-2xl">
                      <FaPhoneAlt size={20} />
                    </div>
                    <div className="info-right">
                      <p className="text-base">{COMPANY_DATA.phoneNumber}</p>
                    </div>
                  </li>
                  <li className="flex items-center">
                    <div className="info-left mr-4 text-2xl">
                      <FaEnvelope size={20} />
                    </div>
                    <div className="info-right">
                      <p className="text-base">{COMPANY_DATA.mailID}</p>
                    </div>
                  </li>
                  <li className="flex items-center">
                    <div className="info-left mr-4 text-2xl">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="info-right">
                      <p className="text-base leading-relaxed">{COMPANY_DATA.address}</p>
                    </div>
                  </li>

                  <div className="flex justify-start gap-4 mt-4">
                    <a
                      href={`tel:${COMPANY_DATA.phoneNumber}`}
                      aria-label="Call QuantaRock"
                      className="text-green-500 hover:text-green-700 transition-transform transform hover:scale-110"
                    >
                      <FaPhoneAlt size={30} />
                    </a>
                    <a
                      href={COMPANY_DATA.linkedInURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="QuantaRock on LinkedIn"
                    >
                      <FaLinkedin
                        size={30}
                        className="text-white hover:text-gray-300 transition-transform transform hover:scale-110"
                      />
                    </a>
                    <a
                      href={COMPANY_DATA.facebookURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="QuantaRock on Facebook"
                    >
                      <FaFacebookF
                        size={30}
                        className="text-blue-500 hover:text-blue-400 transition-transform transform hover:scale-110"
                      />
                    </a>
                    <a
                      href={COMPANY_DATA.mailURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Email QuantaRock"
                    >
                      <FaEnvelope
                        size={30}
                        className="text-red-400 hover:text-red-300 transition-transform transform hover:scale-110"
                      />
                    </a>
                    <a
                      href={COMPANY_DATA.twitterURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="QuantaRock on X"
                    >
                      <FaTwitter
                        size={30}
                        className="text-blue-400 hover:text-blue-300 transition-transform hover:scale-110"
                      />
                    </a>
                    <a
                      href={COMPANY_DATA.instaURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="QuantaRock on Instagram"
                    >
                      <FaInstagram
                        size={30}
                        className="text-[#E4405F] hover:text-[#D10050] transition-transform transform hover:scale-110"
                      />
                    </a>
                    <a
                      href={COMPANY_DATA.youtubeURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="QuantaRock on YouTube"
                    >
                      <FaYoutube
                        size={30}
                        className="text-red-500 hover:text-red-400 transition-transform transform hover:scale-110"
                      />
                    </a>
                  </div>
                </ul>
              </div>
            </div>

            {/* Right Side: Contact Form */}
            <div className="flex items-center justify-center px-6">
              <div className="contact-form relative w-full rounded-3xl border border-white/20 bg-white p-7 shadow-2xl md:p-9">
                <SubHeadingTag className="text-2xl font-bold text-[var(--website-primary-color)]">Request a consultation</SubHeadingTag>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">Tell us what you are planning. Our team will get back to you shortly.</p>
                <form onSubmit={handleSubmit}>
                  <input type="hidden" name="form-name" value="contactForm" />
                  <div className="mt-7 space-y-6">
                    <div>
                      <input
                        type="text"
                        name="name"
                        className="form-control w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-[var(--website-primary-color)] outline-none transition focus:border-[var(--website-secondary-color)] focus:ring-2 focus:ring-[rgba(var(--website-secondary-rgb),0.2)]"
                        placeholder="Full Name *"
                        required
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <input
                        type="email"
                        name="email"
                        className="form-control w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-[var(--website-primary-color)] outline-none transition focus:border-[var(--website-secondary-color)] focus:ring-2 focus:ring-[rgba(var(--website-secondary-rgb),0.2)]"
                        placeholder="Email Address *"
                        required
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-[var(--website-primary-color)] outline-none transition focus:border-[var(--website-secondary-color)] focus:ring-2 focus:ring-[rgba(var(--website-secondary-rgb),0.2)]"
                        placeholder="Phone Number (optional)"
                        value={formData.phone}
                        onChange={handleChange}
                        pattern="^\+?[0-9]{10,15}$"
                        inputMode="tel"
                        title="Phone number must be between 10-15 digits, with an optional '+' at the beginning."
                      />
                    </div>

                    <div>
                      <textarea
                        name="message"
                        rows="3"
                        className="form-control w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-[var(--website-primary-color)] outline-none transition focus:border-[var(--website-secondary-color)] focus:ring-2 focus:ring-[rgba(var(--website-secondary-rgb),0.2)]"
                        placeholder="Enter Your Message *"
                        required
                        value={formData.message}
                        onChange={handleChange}
                      ></textarea>
                    </div>
                    {errorMessage && (
                      <div className="text-red-500 text-sm">{errorMessage}</div>
                    )}

                    {successMessage && (
                      <div className="text-green-500 text-sm flex items-center">
                        <FaCheckCircle className="mr-2 text-xl" />{" "}
                        {successMessage}
                      </div>
                    )}

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-big flex w-full items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-6 py-3 font-semibold text-[var(--on-secondary)] transition duration-300 hover:brightness-110"
                      >
                        {isSubmitting ? "Sending..." : "Send Message"}
                        <FaPaperPlane className="ml-2 text-xl" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div>
        <Location />
      </div>
    </>
  );
}

export default ContactSection;
